/**
 * StudentDrawer – the school's decision on a pending student: approve into a
 * class (the one they asked for by default), approve without a class for now,
 * or reject. Class teachers make the same decision for their own classes from
 * My Classes; this is for everyone else, and for the school to step in.
 */

import { useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import type { RosterPerson, SchoolRoster } from '../../hooks/useSchoolRoster';
import { decideStudent } from '../../services/schoolRosterService';
import { MAX_CLASS_SIZE } from '../../services/classManagementService';
import { requestedClassOf } from '../../lib/classroom/students';
import { StatusBadge } from './TeacherDrawer';
import { classLabel } from './ClassDrawer';

const NO_CLASS = '__none__';
const SELECT = 'h-9 w-full rounded-md border border-input bg-background px-2.5 text-sm text-foreground';

export function StudentDrawer({
  student,
  roster,
  schoolId,
  onClose,
}: {
  student: RosterPerson;
  roster: SchoolRoster;
  schoolId: string;
  onClose: () => void;
}) {
  const requestedId = requestedClassOf(student);
  const requested = requestedId ? roster.classById.get(requestedId) ?? null : null;
  const [classId, setClassId] = useState<string>(requested?.id ?? NO_CLASS);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = (student.approvalStatus ?? 'pending') === 'pending';

  const run = async (decision: 'approve' | 'reject') => {
    setBusy(true);
    try {
      const r = await decideStudent(
        student.id,
        decision,
        decision === 'reject'
          ? { schoolId, reason: reason.trim() || undefined }
          : classId === NO_CLASS
            ? { schoolId, placeNowhere: true }
            : { schoolId, classId }
      );
      if (!r.ok) {
        toast.error(r.message || `Could not ${decision} the student.`);
        return;
      }
      const placed = r.data?.classId ? roster.classById.get(r.data.classId) : null;
      toast.success(
        decision === 'reject'
          ? `${student.name} rejected`
          : placed
            ? `${student.name} approved and placed in ${classLabel(placed)}`
            : `${student.name} approved; place them in a class when ready`
      );
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && !busy && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-md">
        <SheetHeader className="space-y-1 text-left">
          <div className="flex items-center gap-2">
            <SheetTitle className="truncate">{student.name}</SheetTitle>
            <StatusBadge status={student.approvalStatus} />
          </div>
          <SheetDescription className="truncate">{student.email || 'No email on file'}</SheetDescription>
        </SheetHeader>

        <dl className="mt-4 grid grid-cols-2 gap-3 rounded-lg border border-border p-3 text-xs">
          <div>
            <dt className="text-muted-foreground">Asked for</dt>
            <dd className="mt-0.5 text-foreground">{requested ? classLabel(requested) : requestedId ? 'A class that no longer exists' : 'No class'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Signed up</dt>
            <dd className="mt-0.5 text-foreground">{student.createdAt ? new Date(student.createdAt).toLocaleDateString() : '—'}</dd>
          </div>
          {requested && (requested.teacher_ids ?? []).length > 0 && (
            <div className="col-span-2 text-muted-foreground">
              Its teachers can approve this student from My Classes; you can also decide here.
            </div>
          )}
        </dl>

        {!pending ? (
          <p className="mt-5 text-sm text-muted-foreground">
            {student.approvalStatus === 'rejected'
              ? `Rejected${student.rejectionReason ? `: ${student.rejectionReason}` : ''}.`
              : 'Already approved. Place or move them from a class’s page.'}
          </p>
        ) : rejecting ? (
          <section className="mt-5 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
            <p className="text-sm font-medium text-foreground">Reject {student.name}?</p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional, kept on record)"
              className="mt-2 min-h-[60px] text-sm"
              maxLength={500}
              disabled={busy}
            />
          </section>
        ) : (
          <section className="mt-5 space-y-1.5">
            <Label htmlFor="student-class">Place in</Label>
            <select id="student-class" className={SELECT} value={classId} onChange={(e) => setClassId(e.target.value)} disabled={busy}>
              <option value={NO_CLASS}>No class for now</option>
              {roster.classes.map((c) => {
                const n = (c.student_ids ?? []).length;
                return (
                  <option key={c.id} value={c.id} disabled={n >= MAX_CLASS_SIZE}>
                    {classLabel(c)} ({n}/{MAX_CLASS_SIZE}){c.id === requestedId ? ' — asked for' : ''}
                  </option>
                );
              })}
            </select>
          </section>
        )}

        {pending && (
          <div className="mt-auto flex justify-end gap-2 border-t border-border pt-4">
            {rejecting ? (
              <>
                <Button variant="ghost" onClick={() => setRejecting(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={() => run('reject')} disabled={busy}>
                  {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  Reject student
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="mr-auto text-destructive" onClick={() => setRejecting(true)} disabled={busy}>
                  Reject
                </Button>
                <Button onClick={() => run('approve')} disabled={busy}>
                  {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {classId === NO_CLASS ? 'Approve' : 'Approve & place'}
                </Button>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
